import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    requestAnimationFrame(() => this.#setupMobileDrawerHeader());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#setupMobileDrawerHeader();
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Add a branded header to the mobile menu drawer.
   * Reuses the store logo from the main header so it stays in sync with theme settings.
   */
  #setupMobileDrawerHeader() {
    if (!this.closest('[data-hydration-key="header-drawer-mobile"]')) return;

    const drawer = this.refs.menuDrawer;
    if (drawer.querySelector(':scope > .rw-mobile-menu-header')) return;

    const closeButton = drawer.querySelector(':scope > .menu-drawer__close-button');
    if (!(closeButton instanceof HTMLElement)) return;

    const sourceLogo = document.querySelector('#header-component .header-logo, .header-logo');
    const header = document.createElement('div');
    header.className = 'rw-mobile-menu-header';

    const logoLink = document.createElement('a');
    logoLink.className = 'rw-mobile-menu-logo focus-inset';
    logoLink.href = sourceLogo instanceof HTMLAnchorElement ? sourceLogo.getAttribute('href') || '/' : '/';
    logoLink.setAttribute('aria-label', sourceLogo?.getAttribute('aria-label') || 'Ribbonwala home');

    const logoVisual =
      sourceLogo?.querySelector('.header-logo__image-container--original') ??
      sourceLogo?.querySelector('.header-logo__image-container') ??
      sourceLogo?.querySelector('picture, img, svg');

    if (logoVisual) {
      logoLink.append(logoVisual.cloneNode(true));
    } else {
      const logoText = document.createElement('span');
      logoText.className = 'rw-mobile-menu-logo-text';
      logoText.textContent = sourceLogo?.textContent?.trim() || 'Ribbonwala';
      logoLink.append(logoText);
    }

    header.append(logoLink, closeButton);
    drawer.prepend(header);
    this.#injectMobileDrawerHeaderStyles();
  }

  /**
   * Add mobile drawer header styles once per page.
   */
  #injectMobileDrawerHeaderStyles() {
    if (document.getElementById('rw-mobile-menu-header-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'rw-mobile-menu-header-styles';
    styles.textContent = `
      .header__drawer .menu-drawer {
        border-radius: 0 !important;
        border-top: 0 !important;
        border-left: 0 !important;
        border-right: 0 !important;
        box-shadow: none !important;
      }

      .rw-mobile-menu-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        min-height: 68px;
        padding: 10px 12px 10px 24px;
        flex-shrink: 0;
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        border-bottom: 1px solid rgb(var(--color-foreground-rgb) / 0.12) !important;
        background: var(--color-background);
      }

      .rw-mobile-menu-logo {
        display: inline-flex;
        align-items: center;
        max-width: min(190px, calc(100% - 56px));
        color: inherit;
        text-decoration: none;
        line-height: 1;
      }

      .rw-mobile-menu-logo .header-logo__image-container {
        display: block !important;
        position: static !important;
        width: auto !important;
        height: auto !important;
        opacity: 1 !important;
        visibility: visible !important;
      }

      .rw-mobile-menu-logo .header-logo__image,
      .rw-mobile-menu-logo img {
        display: block !important;
        width: auto !important;
        height: auto !important;
        max-width: 180px !important;
        max-height: 40px !important;
        object-fit: contain;
      }

      .rw-mobile-menu-logo-text {
        font-weight: 600;
        font-size: 20px;
        line-height: 1.2;
      }

      .rw-mobile-menu-header .menu-drawer__close-button {
        margin: 0;
        padding: 12px;
        flex: 0 0 auto;
      }
    `;
    document.head.append(styles);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
